import { monsterGifs, eggGif as defaultEggGif, getMonsterName } from '../config/monsterGifs';
import { resolveSpriteUrl, resolveImageKitUrl } from './spriteResolver';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Convert an ISO datetime string (UTC) to the "YYYY-MM-DDTHH:MM" format
 * that <input type="datetime-local"> expects, in the user's LOCAL timezone.
 * Returns '' for empty/invalid input.
 */
export const isoToLocalInputValue = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  // Subtract timezone offset so toISOString() yields the LOCAL clock-time
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
};

/**
 * Current time as a "YYYY-MM-DDTHH:MM" string in the user's local timezone,
 * suitable for the `min` attribute of <input type="datetime-local">.
 */
export const nowLocalInputValue = () => {
  const d = new Date();
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
};

// Cache for uploaded GIFs config
let uploadedGifsCache = null;
let cacheTime = 0;
const CACHE_DURATION = 30000; // 30 seconds
let loadingPromise = null; // Track ongoing load to prevent duplicate requests
let cacheListeners = []; // Listeners for cache updates
let cacheVersion = 0; // Version counter to force re-renders

// ─────────────────────────────────────────────────────────────────────
// Cloudinary disabled detector
// If the Cloudinary account is suspended/over-quota or any individual
// asset is unavailable, every <PokemonImage> calls reportCloudinaryFailure()
// from its onError handler. Once we cross the failure threshold we treat
// the whole CDN as offline and `getMonsterGif` starts returning the
// public PokeAPI URLs instead — re-rendering the entire app silently.
// ─────────────────────────────────────────────────────────────────────
const CLOUDINARY_FAILURE_THRESHOLD = 2;  // 2 distinct failures = treat as down
let _cloudinaryFailureCount = 0;
let _failingHosts = new Set();
let _cloudinaryDisabled = false;

export const isCloudinaryDisabled = () => _cloudinaryDisabled;

export const reportCloudinaryFailure = (url) => {
  if (!url || _cloudinaryDisabled) return;
  if (typeof url === 'string' && url.includes('cloudinary.com')) {
    _failingHosts.add(url.split('?')[0]);
    _cloudinaryFailureCount = _failingHosts.size;
    if (_cloudinaryFailureCount >= CLOUDINARY_FAILURE_THRESHOLD) {
      _cloudinaryDisabled = true;
      console.warn(
        '[GIF Cache] Cloudinary appears unavailable — falling back to public PokeAPI URLs',
        { failureCount: _cloudinaryFailureCount }
      );
      // Force every subscribed component to recompute with the new fallback
      notifyCacheListeners();
    }
  }
};

// Subscribe to cache updates (for component re-renders)
export const subscribeToCacheUpdates = (callback) => {
  cacheListeners.push(callback);
  return () => {
    cacheListeners = cacheListeners.filter(cb => cb !== callback);
  };
};

// Notify all listeners when cache updates
const notifyCacheListeners = () => {
  cacheVersion++;
  cacheListeners.forEach(cb => cb(uploadedGifsCache));
};

// Force cache refresh and clear browser cache
export const refreshGifCache = async () => {
  cacheTime = 0; // Reset cache time to force refresh
  loadingPromise = null; // Clear any pending promise
  return loadUploadedGifs();
};

// Get cache version (for React keys to force re-render)
export const getCacheVersion = () => cacheVersion;

// Check if cache is ready
export const isGifCacheReady = () => {
  return uploadedGifsCache !== null;
};

// Get cache directly (for sync access when already loaded)
export const getGifCache = () => uploadedGifsCache;

// Helper to check if URL is absolute (Cloudinary)
const isAbsoluteUrl = (url) => {
  return url && (url.startsWith('http://') || url.startsWith('https://'));
};

// Helper to build correct GIF URL (handles both Cloudinary and local)
const buildGifUrl = (gifPath) => {
  if (!gifPath) return null;
  // If it's already an absolute URL (Cloudinary), return as-is
  if (isAbsoluteUrl(gifPath)) {
    return gifPath;
  }
  // Otherwise, it's a local path, prepend backend URL
  return `${BACKEND_URL}${gifPath}`;
};

export const loadUploadedGifs = async () => {
  const now = Date.now();
  
  // Return cached data if still valid
  if (uploadedGifsCache && (now - cacheTime) < CACHE_DURATION) {
    return uploadedGifsCache;
  }

  // If already loading, return the existing promise
  if (loadingPromise) {
    return loadingPromise;
  }

  // Start new load
  loadingPromise = (async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/gifs/config`);
      if (response.ok) {
        uploadedGifsCache = await response.json();
        cacheTime = Date.now();
        console.log('[GIF Cache] Loaded uploaded GIFs config:', Object.keys(uploadedGifsCache));
        notifyCacheListeners();
        return uploadedGifsCache;
      }
    } catch (error) {
      console.error('[GIF Cache] Failed to load:', error);
    } finally {
      loadingPromise = null;
    }
    return null;
  })();

  return loadingPromise;
};

/**
 * Resolve the sprite for a full monster object.
 *
 *   1. ImageKit CDN — arquivo animado nomeado pelo nome atual da espécie
 *      (muda automaticamente quando a evolução por nível troca o nome).
 *   2. Fallback (via <PokemonImage fallbackSrc>): cadeia legada abaixo.
 */
export const getMonsterGifByMonster = (monster) => {
  if (!monster) return getMonsterGif('tank', 0, 1);
  const cdn = resolveImageKitUrl(monster.name);
  if (cdn) return cdn;
  return getMonsterFallbackGif(monster);
};

/**
 * Cadeia legada de resolução (usada como fallback quando o arquivo ainda
 * não existe no ImageKit): GIF customizado do professor → PokeAPI por nome
 * → lookup legado por (class_type + monster_index + stage).
 */
export const getMonsterFallbackGif = (monster) => {
  if (!monster) return getMonsterGif('tank', 0, 1);
  const byName = resolveSpriteUrl(monster.name, uploadedGifsCache);
  if (byName) return byName;
  return getMonsterGif(monster.class_type, monster.monster_index, monster.stage || 1);
};

export const getMonsterGif = (classType, monsterIndex, stage = 1) => {
  // Try to use uploaded GIFs first — unless the Cloudinary CDN is offline,
  // in which case we skip it entirely so the UI does not flicker through
  // a known-broken URL before showing the public fallback.
  if (!_cloudinaryDisabled && uploadedGifsCache && uploadedGifsCache[classType] && uploadedGifsCache[classType][monsterIndex]) {
    const stageGifs = uploadedGifsCache[classType][monsterIndex];
    if (stageGifs[stage]) {
      return buildGifUrl(stageGifs[stage]);
    }
    // Fallback to stage 1 if specific stage not found
    if (stageGifs[1]) {
      return buildGifUrl(stageGifs[1]);
    }
  }

  // Fallback to default GIFs (PokeAPI on GitHub)
  const gifs = monsterGifs[classType];
  if (gifs && gifs[monsterIndex]) {
    return gifs[monsterIndex];
  }
  
  return monsterGifs.tank[0];
};

/**
 * Returns the PUBLIC default sprite URL (PokeAPI on GitHub), ignoring the
 * uploaded GIFs cache. Used as `fallbackSrc` in <PokemonImage> so the UI
 * recovers automatically if the Cloudinary upload is unavailable
 * (account disabled, quota exceeded, asset 404, etc.).
 */
export const getDefaultMonsterGif = (classType, monsterIndex) => {
  const gifs = monsterGifs[classType];
  if (gifs && gifs[monsterIndex]) {
    return gifs[monsterIndex];
  }
  return monsterGifs.tank[0];
};

export const getEggGif = () => {
  // Try to use uploaded egg GIF first — skip if Cloudinary is offline
  if (!_cloudinaryDisabled && uploadedGifsCache && uploadedGifsCache.egg) {
    return buildGifUrl(uploadedGifsCache.egg);
  }
  
  return defaultEggGif;
};

export const getMonsterNameByIndex = (classType, index, stage = 1) => {
  // Try to get species name from uploaded GIFs config first (teacher-defined,
  // most authoritative source — these names are what the teacher uploaded).
  if (uploadedGifsCache && uploadedGifsCache.speciesNames) {
    const classNames = uploadedGifsCache.speciesNames[classType];
    if (classNames && classNames[index]) {
      const stageKey = `stage_${stage}`;
      if (classNames[index][stageKey]) {
        return classNames[index][stageKey];
      }
      // Fallback to stage 1 if specific stage not found
      if (classNames[index].stage_1) {
        return classNames[index].stage_1;
      }
    }
  }
  
  // Returning null lets `getMonsterDisplayName` decide between
  // monster.name (DB) and the hardcoded fallback table.
  return null;
};

// Get monster display name. Priority order:
//   1. monster.name stored in DB (now kept in sync by the evolution sweep,
//      so it always reflects the current evolutionary stage of the actual
//      Pokémon — e.g. "Metapod" instead of the slot's generic "Brionne").
//   2. gif_configs.speciesNames (legacy fallback for monsters without a name).
//   3. Hardcoded `monsterEvolutionNames` table (final fallback).
export const getMonsterDisplayName = (monster) => {
  if (!monster) return 'Desconhecido';

  // 1) Stored name — single source of truth.
  if (monster.name && monster.name.trim()) return monster.name.trim();

  // 2) Teacher-configured slot name (only used when monster.name is empty).
  const speciesName = getMonsterNameByIndex(
    monster.class_type,
    monster.monster_index,
    monster.stage || 1
  );
  if (speciesName) return speciesName;

  // 3) Hardcoded evolutionary table (last resort).
  const fallback = getMonsterName(
    monster.class_type,
    monster.monster_index,
    monster.stage || 1
  );
  return fallback || 'Sem nome';
};

// Load uploaded GIFs on module load
loadUploadedGifs();

export const calculateXPNeeded = (level) => {
  return level * 100;
};

export const getClassColor = (classType) => {
  const colors = {
    tank: 'bg-blue-500',
    warrior: 'bg-red-500',
    support: 'bg-green-500'
  };
  return colors[classType] || 'bg-gray-500';
};

export const getClassBadge = (classType) => {
  const badges = {
    tank: 'bg-blue-100 text-blue-700 border-blue-200',
    warrior: 'bg-red-100 text-red-700 border-red-200',
    support: 'bg-green-100 text-green-700 border-green-200'
  };
  return badges[classType] || 'bg-gray-100 text-gray-700 border-gray-200';
};

export const getClassName = (classType) => {
  const names = {
    tank: 'Tank',
    warrior: 'Atacante',
    support: 'Suporte'
  };
  return names[classType] || classType;
};

export const formatDate = (isoString) => {
  if (!isoString) return 'No date';
  const date = new Date(isoString);
  return date.toLocaleDateString('pt-BR', { 
    year: 'numeric', 
    month: 'short', 
    day: 'numeric' 
  });
};
